import React, { useState, useEffect } from 'react';
import { Table } from "reactstrap";
import ViaticosService from "../axios_services/viaticos.service";
import Loader from "../components/Loader/Loader";
import { ButtonUpdate, ButtonDelete, ButtonInsert, ButtonSearch } from '../components/Buttons/Buttons';
import ModalViaticos from '../components/Modal/ModalViaticos';
import './CajaFinancieroMovimiento.css';

function Viaticos() {
    const [viaticos, setViaticos] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedViatico, setSelectedViatico] = useState(null);
    const [filtroTipoViatico, setFiltroTipoViatico] = useState('');
    const [tipoViaticoActivo, setTipoViaticoActivo] = useState('');

    const formatoSoles = (monto) => {
        const valor = new Intl.NumberFormat('es-PE', {
            style: 'currency',
            currency: 'PEN',
        }).format(monto);
        return valor;
    };

    useEffect(() => {
        fetchViaticos();
    }, []);

    const fetchViaticos = async () => {
        setIsLoading(true);
        try {
            const response = await ViaticosService.getViaticos();
            setViaticos(response.data || []);
        } catch (error) {
            console.error('Error al obtener viáticos:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleNuevoViatico = () => {
        setSelectedViatico(null);
        setIsModalOpen(true);
    };

    const handleEditarViatico = (viatico) => {
        setSelectedViatico(viatico);
        setIsModalOpen(true);
    };

    const handleEliminarViatico = async (viaticoId) => {
        if (!window.confirm('¿Está seguro que desea eliminar este registro?')) {
            return;
        }
        try {
            setIsLoading(true);
            await ViaticosService.deleteViaticos(viaticoId);
            await fetchViaticos();
        } catch (error) {
            console.error('Error al eliminar viático:', error);
            alert('Error al eliminar el registro');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedViatico(null);
    };

    const handleSaveViatico = async (viaticoData) => {
        try {
            setIsLoading(true);
            setIsModalOpen(false);
            if (selectedViatico) {
                await ViaticosService.updateViaticos(selectedViatico.viatico_id, viaticoData);
            } else {
                await ViaticosService.insertViaticos(viaticoData);
            }
            await fetchViaticos();
        } catch (error) {
            console.error('Error al guardar viático:', error);
            alert(error.response?.data?.message || 'Ocurrió un error al guardar.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleBuscar = async () => {
        setTipoViaticoActivo(filtroTipoViatico);
        await fetchViaticos();
    };

    const viaticosFiltrados = viaticos.filter(v => {
        if (!tipoViaticoActivo) return true;
        return v.tipo_viatico?.toString() === tipoViaticoActivo;
    });

    if (isLoading) {
        return <Loader />;
    }

    return (
        <div className="caja-flujo-container">
            <div className="flex items-center justify-between mb-4">
                <h1>DETALLE DE VIÁTICOS</h1>
            </div>

            <div className="filtros">
                <div className="campo-fecha">
                    <label htmlFor="filtroTipoViatico">Tipo de Viático:</label>
                    <select
                        id="filtroTipoViatico"
                        value={filtroTipoViatico}
                        onChange={(e) => setFiltroTipoViatico(e.target.value)}
                        className="form-control"
                    >
                        <option value="">Todos</option>
                        <option value="1">Colaborador</option>
                        <option value="2">Gerentes</option>
                    </select>
                </div>
            </div>

            <ButtonSearch onClick={handleBuscar} isLoading={isLoading} />

            <div className="nuevo-movimiento-section">
                <ButtonInsert onClick={handleNuevoViatico} />
            </div>

            <div className="table-container">
                <Table>
                    <thead>
                        <tr>
                            <th>TIPO VIÁTICO</th>
                            <th>LUGAR</th>
                            <th>PASAJE</th>
                            <th>HOSPEDAJE</th>
                            <th>ALIMENTACIÓN</th>
                            <th>ADICIONAL</th>
                            <th>TOTAL</th>
                            <th>ACCIONES</th>
                        </tr>
                    </thead>
                    <tbody>
                        {viaticosFiltrados.length > 0 ? (
                            viaticosFiltrados.map((viatico) => (
                                <tr key={viatico.viatico_id}>
                                    <td>
                                        {viatico.tipo_viatico?.toString() === '1' ? 'COLABORADOR' :
                                            viatico.tipo_viatico?.toString() === '2' ? 'GERENTES' : 'N/A'}
                                    </td>
                                    <td>{viatico.lugar || 'N/A'}</td>
                                    <td className="col-monto">{formatoSoles(viatico.pasaje)}</td>
                                    <td className="col-monto">{formatoSoles(viatico.hospedaje)}</td>
                                    <td className="col-monto">{formatoSoles(viatico.alimentacion)}</td>
                                    <td className="col-monto">{formatoSoles(viatico.adicional)}</td>
                                    <td className="col-monto">
                                        {formatoSoles(
                                            (Number(viatico.pasaje) || 0) +
                                            (Number(viatico.hospedaje) || 0) +
                                            (Number(viatico.alimentacion) || 0) +
                                            (Number(viatico.adicional) || 0)
                                        )}
                                    </td>
                                    <td>
                                        <div className="acciones-container">
                                            <ButtonUpdate onClick={() => handleEditarViatico(viatico)} />
                                            <ButtonDelete onClick={() => handleEliminarViatico(viatico.viatico_id)} />
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="8" className="text-center text-gray-500 py-4">
                                    No hay datos disponibles.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>

            <ModalViaticos
                onSave={handleSaveViatico}
                onClose={handleCloseModal}
                isOpen={isModalOpen}
                viatico={selectedViatico}
            />
        </div>
    );
}

export default Viaticos;
